import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { McSelectNative } from '@maersk-global/mds-react-wrapper';
import { demandsApi } from '../api/demandsApi';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { DemandStatusBadge } from '../components/DemandStatusBadge';
import { DemandDetailContent } from '../components/DemandDetailContent';
import { TaskDetailContent } from '../components/TaskDetailContent';
import type { DemandStatus, DemandSummary } from '../types/engineering';

const KANBAN_COLUMNS: { status: DemandStatus; label: string }[] = [
  { status: 'BACKLOG', label: 'Backlog' },
  { status: 'PLANNING', label: 'Planning' },
  { status: 'WAITING_REVIEW', label: 'Waiting Review' },
  { status: 'INPROGRESS', label: 'In Progress' },
  { status: 'DONE', label: 'Done (last 30 days)' },
];

const STATUS_COLOR_CLASS: Record<DemandStatus, string> = {
  BACKLOG: 'sprint-card--backlog',
  PLANNING: 'sprint-card--planning',
  INPROGRESS: 'sprint-card--inprogress',
  WAITING_REVIEW: 'sprint-card--waiting-review',
  DONE: 'sprint-card--done',
  TESTING: 'sprint-card--testing',
  CANCELLED: 'sprint-card--cancelled',
};

const STATUS_HEADER_CLASS: Record<DemandStatus, string> = {
  BACKLOG: 'sprint-column__header--backlog',
  PLANNING: 'sprint-column__header--planning',
  INPROGRESS: 'sprint-column__header--inprogress',
  WAITING_REVIEW: 'sprint-column__header--waiting-review',
  DONE: 'sprint-column__header--done',
  TESTING: 'sprint-column__header--testing',
  CANCELLED: 'sprint-column__header--cancelled',
};

// ─── Kanban Modal ─────────────────────────────────────────────────────────────

interface KanbanModalProps {
  demandId: string;
  onClose: () => void;
}

const KanbanModal = ({ demandId, onClose }: KanbanModalProps) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  return (
    <div className="kanban-modal-overlay" onClick={onClose}>
      <div className="kanban-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kanban-modal__topbar">
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="kanban-modal__body">
          {selectedTaskId ? (
            <TaskDetailContent
              id={selectedTaskId}
              onBack={() => setSelectedTaskId(null)}
            />
          ) : (
            <DemandDetailContent
              id={demandId}
              hideBack
              onNavigateToTask={(taskId) => setSelectedTaskId(taskId)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Kanban Page ──────────────────────────────────────────────────────────────

export const KanbanPage = () => {
  const [selectedSystem, setSelectedSystem] = useState('');
  const [openDemandId, setOpenDemandId] = useState<string | null>(null);

  const { data: systems } = useQuery({
    queryKey: ['demands', 'systems'],
    queryFn: () => demandsApi.getSystems(),
  });

  const systemOptions = useMemo(() => {
    const fromSystems = (systems ?? []).map((s) => ({ value: s, label: s }));
    return [...fromSystems];
  }, [systems]);

  const { data: demands, isLoading } = useQuery({
    queryKey: ['kanban', 'demands', selectedSystem],
    queryFn: () => demandsApi.getBySystem(selectedSystem),
    enabled: !!selectedSystem,
  });

  const grouped = KANBAN_COLUMNS.reduce<Record<DemandStatus, DemandSummary[]>>(
    (acc, col) => {
      acc[col.status] = (demands ?? []).filter((d) => d.status === col.status);
      return acc;
    },
    {} as Record<DemandStatus, DemandSummary[]>,
  );

  return (
    <div>
      <div className="page-header">
        <h1>Kanban</h1>
        <p>Board by system — DONE column shows only demands completed in the last 30 days</p>
      </div>

      <div className="sprint-filter">
        <McSelectNative
          label="System"
          labelposition="left"
          fit="small"
          options={systemOptions}
          value={[{ value: selectedSystem, label: selectedSystem || 'Select a system...' }]}
          change={(e: Event) => {
            const detail = (e as CustomEvent).detail;
            setSelectedSystem(String(detail?.selectedOptions?.[0]?.value ?? ''));
          }}
          style={{ width: '240px' }}
        />
      </div>

      {!selectedSystem ? (
        <div className="empty-state">Select a system above to view the Kanban board.</div>
      ) : isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="sprint-board">
          {KANBAN_COLUMNS.map(({ status, label }) => {
            const cards = grouped[status] ?? [];
            return (
              <div key={status} className="sprint-column">
                <div className={`sprint-column__header ${STATUS_HEADER_CLASS[status] ?? ''}`}>
                  <span>{label}</span>
                  <span className="sprint-column__badge">{cards.length}</span>
                </div>
                <div className="sprint-column__cards">
                  {cards.length === 0 ? (
                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: 'var(--mds_brand_appearance_neutral_default_text-color)', textAlign: 'center' }}>
                      Empty
                    </div>
                  ) : (
                    cards.map((d) => (
                      <div
                        key={d.id}
                        className={`sprint-card ${STATUS_COLOR_CLASS[d.status] ?? ''}`}
                        onClick={() => setOpenDemandId(d.id)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setOpenDemandId(d.id);
                        }}
                      >
                        <div className="sprint-card__header">
                          <span className="sprint-card__code">{d.code}</span>
                          <DemandStatusBadge status={d.status} />
                        </div>
                        <div className="sprint-card__title">{d.title}</div>
                        <div className="sprint-card__footer">
                          <span>{d.taskCount} task{d.taskCount !== 1 ? 's' : ''}</span>
                          <span className="sprint-card__footer-right">
                            {d.cost != null && (
                              <span className="sprint-card__cost">
                                ${d.cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </span>
                            )}
                            <span>{d.percentConcluded}%</span>
                          </span>
                        </div>
                        <div className="sprint-card__progress">
                          <div className="sprint-card__progress-fill" style={{ width: `${d.percentConcluded}%` }} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openDemandId && (
        <KanbanModal demandId={openDemandId} onClose={() => setOpenDemandId(null)} />
      )}
    </div>
  );
};

